import React, { useEffect, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import ptBr from 'date-fns/locale/pt-BR';
import { Control, Controller } from 'react-hook-form';
import {
  Button,
  Flex,
  Input,
  FormControl,
  FormLabel,
  InputGroup,
  FormErrorMessage,
  FormControlProps,
} from '@chakra-ui/react';
import { Global } from '@emotion/react';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('pt-br', ptBr);

interface Props extends FormControlProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  control: Control<any, any>;
  name: string;
  showTimeSelect?: boolean;
  label?: string;
  error?: any;
  isRequired?: boolean;
  dateFormat?: string;
  /** Props extras do react-datepicker (ex.: `withPortal: false` fora de modal). */
  datePickerProps?: Record<string, unknown>;
}

const FORMATO_COM_HORA = 'dd/MM/yyyy HH:mm';
const FORMATO_SO_DATA = 'dd/MM/yyyy';
const ID_PORTAL = 'datepicker-portal';

/**
 * O Chakra prende o foco dentro do modal e devolveria o foco ao campo
 * principal ao clicar no calendário (que está no body). O atributo faz o
 * bloqueio de foco ignorar o contêiner do portal.
 */
const garantirContainerDoPortal = () => {
  const container =
    document.getElementById(ID_PORTAL) ?? document.createElement('div');
  container.id = ID_PORTAL;
  container.setAttribute('data-no-focus-lock', 'true');
  if (!container.parentElement) document.body.appendChild(container);
};

const Datepicker: React.FC<Props> = ({
  control,
  name,
  showTimeSelect,
  label,
  error,
  isRequired = false,
  dateFormat,
  datePickerProps,
  ...rest
}) => {
  useEffect(garantirContainerDoPortal, []);

  const calendarioRef = useRef<DatePicker>(null);
  const valorAoAbrir = useRef<Date | null>(null);

  return (
    <FormControl isInvalid={!!error} isRequired={isRequired} {...rest}>
      {/* a largura padrão do contêiner é menor que a do calendário no portal */}
      <Global
        styles={{ '.react-datepicker__children-container': { width: 'auto' } }}
      />
      {!!label && <FormLabel htmlFor={name}>{label}</FormLabel>}
      <InputGroup>
        <Controller
          name={name}
          control={control}
          render={({ field: { onBlur, onChange, value } }) => (
            <DatePicker
              ref={calendarioRef}
              onCalendarOpen={() => {
                valorAoAbrir.current = value ?? null;
              }}
              customInput={
                <Input
                  focusBorderColor="brand.500"
                  bgColor="gray.100"
                  variant="filled"
                  _hover={{ borderColor: 'brand.500' }}
                  size="md"
                />
              }
              locale="pt-br"
              selected={value}
              onChange={onChange}
              onBlur={onBlur}
              // showTimeInput aceita qualquer horário; a lista prendia em 30 min
              showTimeInput={showTimeSelect}
              timeInputLabel="Horário:"
              dateFormat={
                dateFormat ??
                (showTimeSelect ? FORMATO_COM_HORA : FORMATO_SO_DATA)
              }
              // com horário digitável, fechar ao mudar o horário devolve o foco
              // ao campo principal e impede digitar mais de um dígito
              shouldCloseOnSelect={!showTimeSelect}
              // withPortal sozinho desenha inline e o fixed fica preso ao modal
              // (que tem transform); o portalId manda o calendário para o body
              withPortal
              portalId={
                datePickerProps?.withPortal === false ? undefined : ID_PORTAL
              }
              {...datePickerProps}
            >
              {showTimeSelect && (
                <Flex justify="center" gap={2} p={2}>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      onChange(valorAoAbrir.current);
                      calendarioRef.current?.setOpen(false);
                    }}
                  >
                    Fechar
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    colorScheme="brand"
                    onClick={() => calendarioRef.current?.setOpen(false)}
                  >
                    Concluir
                  </Button>
                </Flex>
              )}
            </DatePicker>
          )}
        />
      </InputGroup>
      {!!error && <FormErrorMessage>{error.message}</FormErrorMessage>}
    </FormControl>
  );
};

export default Datepicker;
